import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import './style.scss';
import { Icon } from 'semantic-ui-react';

/**
 * ImageCompare
 * A11y-friendly before/after (left/right) comparison slider that works with any React nodes — not just <img>.
 *
 * Features
 * - Works with arbitrary components as the "images" (e.g., canvases, charts, Next/Image wrappers)
 * - Mouse, touch, and keyboard (aria role="slider")
 * - Optional vertical orientation
 * - Optional aspect ratio and responsive layout
 * - Tailwind classes for styling, but works with plain CSS too
 */
export type ImageCompareProps = {
  /** Left side content (e.g., <MyImage />). Shown from 0% to divider. */
  left: React.ReactNode;
  /** Right side content. Shown from divider to 100%. */
  right: React.ReactNode;
  /** Left side properties */
  leftStyle?: React.CSSProperties;
  /** Right side properties */
  rightStyle?: React.CSSProperties;
  /** Initial divider position as a fraction [0..1]. Default 0.5 */
  defaultPosition?: number;
  /** Controlled position as a fraction [0..1] */
  position?: number;
  /** Callback when divider position changes (for controlled usage) */
  onPositionChange?: (p: number) => void;
  /** Constrain height by aspect ratio (e.g., 16/9). Omit to size by content. */
  aspectRatio?: number;
  /** Minimum pixel distance between handle and container edges */
  handlePadding?: number;
  /** Extra classnames for outer wrapper */
  className?: string;
};

export default function ImageComparator({
  left,
  right,
  leftStyle,
  rightStyle,
  defaultPosition = 0.5,
  position,
  onPositionChange,
  aspectRatio,
  handlePadding = 8,
  className = '',
}: ImageCompareProps) {
  const isControlled = typeof position === 'number';
  const [internal, setInternal] = useState(() => clamp01(defaultPosition));
  const value = clamp01(isControlled ? (position as number) : internal);

  const trackRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);
  const onDividerRefCleanup = useRef<() => void>();

  const setValue = useCallback(
    (next: number) => {
      const v = clamp01(next);
      if (isControlled) {
        onPositionChange?.(v);
      } else {
        setInternal(v);
        onPositionChange?.(v);
      }
    },
    [isControlled, onPositionChange]
  );

  const percent = value * 100;

  const computeFromPointer = useCallback(
    (clientX: number) => {
      const el = trackRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = clamp(clientX - rect.left, handlePadding, rect.width - handlePadding);
      const v = (x - handlePadding) / Math.max(1, rect.width - 2 * handlePadding);
      setValue(v);
    },
    [handlePadding, setValue]
  );

  const onDividerRef = useCallback(
    (handle: HTMLDivElement | null) => {
      onDividerRefCleanup.current?.();
      if (!handle) {
        onDividerRefCleanup.current = undefined;
        return;
      }

      const onMove = (e: MouseEvent | TouchEvent) => {
        if (!draggingRef.current) return;
        if (e instanceof TouchEvent) {
          const t = e.touches[0] ?? e.changedTouches[0];
          if (!t) return;
          computeFromPointer(t.clientX);
        } else {
          computeFromPointer((e as MouseEvent).clientX);
        }
      };
      const onUp = () => {
        draggingRef.current = false;
        window.removeEventListener('mousemove', onMove as any);
        window.removeEventListener('touchmove', onMove as any, { capture: false } as any);
        window.removeEventListener('mouseup', onUp);
        window.removeEventListener('touchend', onUp);
        window.removeEventListener('touchcancel', onUp);
      };

      const startDrag = (x: number) => {
        draggingRef.current = true;
        computeFromPointer(x);
        window.addEventListener('mousemove', onMove as any);
        window.addEventListener('touchmove', onMove as any, { passive: false } as any);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchend', onUp);
        window.addEventListener('touchcancel', onUp);
      };

      const onMouseDown = (e: MouseEvent) => {
        e.preventDefault();
        startDrag(e.clientX);
      };
      const onTouchStart = (e: TouchEvent) => {
        const t = e.touches[0];
        if (!t) return;
        startDrag(t.clientX);
      };

      handle.addEventListener('mousedown', onMouseDown);
      handle.addEventListener('touchstart', onTouchStart, { passive: true });
      onDividerRefCleanup.current = () => {
        handle.removeEventListener('mousedown', onMouseDown);
        handle.removeEventListener('touchstart', onTouchStart);
        onUp();
      };
    },
    [computeFromPointer]
  );

  const wrapperStyle = useMemo<React.CSSProperties>(() => {
    if (!aspectRatio) return {};
    return { aspectRatio: String(aspectRatio) } as React.CSSProperties;
  }, [aspectRatio]);

  // Clipping logic: we stack two layers. Bottom = left, Top = right (clipped). By changing the clip-path of the top layer, we reveal more or less of it, bottom layer (left) is always fully shown but hidden under the top layer.
  const clipStyle = useMemo<React.CSSProperties>(() => {
    // Show right-side content from the divider to the right edge
    return { clipPath: `xywh(${percent}% 0 ${100 - percent}% 100%)` } as React.CSSProperties;
  }, [percent]);

  const dividerStyle = useMemo<React.CSSProperties>(() => {
    return {
      left: `calc(${percent}% - ${handlePadding + 1}px)`,
      top: 0,
      bottom: 0,
      paddingLeft: `${handlePadding}px`,
      paddingRight: `${handlePadding}px`,
    } as React.CSSProperties;
  }, [percent, handlePadding]);

  return (
    <div className={'image-comparator ' + className} style={wrapperStyle}>
      <div ref={trackRef} className={'image-comparator-container'} role="presentation">
        {/* Base layer: LEFT (full) */}
        <div className="image-comparator-left" style={leftStyle}>
          {left}
        </div>

        {/* Top layer: RIGHT (clipped) */}
        <div className="image-comparator-right" style={{ ...rightStyle, ...clipStyle }}>
          {right}
        </div>

        {/* Divider + Handle */}
        <div
          className="image-comparator-divider"
          style={dividerStyle}
          ref={onDividerRef}
          aria-hidden
        >
          <div className="image-comparator-divider__line" />
          <div className="image-comparator-divider__handle">
            <Icon name="angle left" />
            <Icon name="angle right" />
          </div>
        </div>
      </div>
    </div>
  );
}

// Helpers
function clamp01(n: number) {
  return Math.min(1, Math.max(0, n));
}
function clamp(n: number, min: number, max: number) {
  return Math.min(Math.max(n, min), max);
}
